
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next'
import clsx from 'clsx';
import { useQuery, useMutation, useQueryClient} from 'react-query';
import { toast } from 'components/Shared/Toast';

import { parseDate, formatCurrency } from 'utils'
import useOnClickOutside from 'hooks/useOnClickOutside'
import useKeyPress from 'hooks/useKeyPress'
import DatePicker from 'components/Shared/DatePicker'
import bootbox from 'components/Modals/Bootbox'
import { contractAction } from 'api/actions/contracts'
import { fetcher } from 'api/client'



function Amount({ contract= {} }) {
    const { t } = useTranslation()
    return (
        <div className="p-2 h-100">
            <div className="detail text-justify">
                <p> {t('contracts.payments.description')}</p>
            </div>
            <div className="payments-listing overflow-auto h-100">
                <PaymentList contract={contract} list={contract.payments} />
            </div>
        </div>
    )
};

export default Amount;

function PaymentList({ contract, list }) {
    const { t } = useTranslation()
    const queryClient = useQueryClient();
    const {mutate:mutateUpdatePayment} = useMutation((payload) => fetcher(contractAction(contract._id, 'updatepayment', payload)), {
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries(['contract', variables.contractId])
        },
        onError: (error) => {
            toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
        },
    })

    const handleConfirmPaid = async (payment) => {
        const res = await bootbox.confirm(t('contracts.payments.confirm_payment'))
        if (!res) return ;

        const payload = {
            ...payment,
            contractId: contract._id,
            paymentId: payment._id,
            paid: true,
            paid_date: new Date()
        }
        
        mutateUpdatePayment(payload)
    }
     

    const handleDueDateChange = (payment, { date }) => {
        const payload = {
            ...payment,
            contractId: contract._id,
            paymentId: payment._id,
            due_date: date
        }
        
        mutateUpdatePayment(payload)
    }

    const handleAmountChange = (payment, { newAmount }) => {
        const payload = {
            ...payment,
            contractId: contract._id,
            paymentId: payment._id,
            amount: newAmount
        }
        
        mutateUpdatePayment(payload)
    }

    return (
        <div>
            {
                list?.map(payment => (
                    <PaymentItem 
                        payment={payment} 
                        onConfirmPaid={handleConfirmPaid}
                        onDueDateChange={handleDueDateChange}
                        onAmountChange={handleAmountChange}
                    />
                ))
            }
        </div>
    )
}

function PaymentItem({ payment, onConfirmPaid, onDueDateChange, onAmountChange }) {
    const { t, i18n } = useTranslation()
    const { paid, number, amount, due_date, paid_date } = payment
    const [isEditing, setIsEditing] = useState(false)
    const currencyInputRef = useRef()

    const handleAmountChange = useCallback(async () =>  { 
        const newAmount = Math.max(currencyInputRef.current.value, 0); 
        await onAmountChange(payment, { newAmount })
        setIsEditing(false)
    }, [payment, onAmountChange])

    const enterPressed = useKeyPress('Enter');
    useOnClickOutside(currencyInputRef, handleAmountChange)
    useEffect(() => {
        if (enterPressed) {
            handleAmountChange()
        }
    }, [enterPressed, handleAmountChange])

    const handleConfirmPaid = () => {
        if (!paid) {
            onConfirmPaid(payment)
        }
    }

    const handleDueDateChange = (date) => {
        onDueDateChange(payment, { date })
    }

    const handleEditAmount = () => {
        if (!paid) {
            setIsEditing(true)
        }
    }


    return (
        <div className="payment-item">
            <div className="payment-status">
                <span className={clsx(paid ? 'payment-paid' : 'payment-pending pointer')} data-action="confirm-paid" onClick={handleConfirmPaid}>
                    <i className={`fas fa-${paid ? 'check' : 'minus'}`}></i>
                </span>
            </div>
            <div className="payment-detail">
                <span className="payment-detail-title">
                    {t('contracts.payments.payment')} {number} - 
                    {!isEditing  ?
                        <label className={clsx('as-link ml-2', !paid && 'pointer')} onClick={handleEditAmount}>${formatCurrency(amount)}</label>
                        : <input type="number" id="name" ref={currencyInputRef} defaultValue={amount} autoFocus min={0} />
                    }
                </span>
                <span className="payment-detail-duedate">
                    <b>{t('contracts.payments.payment_due_date')}</b>: 
                    <DatePicker
                        locale={i18n.language}
                        disabled={paid}
                        className={clsx('border-0 border-bottom text-capitalize outline-none as-link')}
                        selected={due_date ? new Date(due_date) : null}
                        onChange={handleDueDateChange}
                        // minDate={Date.now()}
                        customInput={<input type="text" style={{fontSize: 12}} />}
                        dateFormat="dd/MMM/yyyy"
                        placeholderText={t('contracts.milestones.pending')} 
                    />
                    {/* <a href="#" data-action="change-due-date">{parseDate(due_date)}</a>
                    <input type="text" className="payment-change-due-date" data-format="DD/MMM/YYYY" name="set-payment-due-date" style={{ display:"none"}} placeholder={t('contracts.payments.payment_due_date')} /> */}
                </span>
                {paid &&
                    <span className="payment-detail-paiddate">
                        <b>{t('contracts.payments.payment_date')}</b>: {parseDate(paid_date, 'dd/MMM/yyyy')}
                    </span>
                }
            </div>
        </div>
    )
}